import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon, Calculate as CalculateIcon } from '@mui/icons-material';
import invoiceService from '../../services/invoiceService';
import GatewaySelect from '../selects/GatewaySelect';
import ContractsActiveSelect from '../selects/ContractsActiveSelect';
import { formatarData, formatarValor, formatarValorOnInput, parsearMoeda } from '../../utils/formatToView';
import { Gateway } from '../../services/gatewayService';

export interface RenegociarFaturaModalProps {
  open: boolean;
  onClose: () => void;
  invoiceIds: number[];
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

interface InvoiceData {
  id: number;
  cliente_id: number;
  contrato_id?: number;
  valor: number;
  acrescimo: number;
  desconto: number;
  data_vencimento: string;
  data_emissao: string;
  descricao: string;
  mes_referencia?: string;
  data_inicial?: string | null;
  data_final?: string | null;
  gateway_id?: number;
  gateway?: { id: number; nome: string } | null;
}

const getCurrentDate = (): string => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const formatarMoeda = (valor: number): string =>
  valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const calcularJurosMulta = (
  valor: number,
  dataVencimento: string,
  dataNovaVencimento: string,
  jurosPercent: number,
  multaPercent: number
): { juros: number; multa: number; dias: number } => {
  const venc = new Date(dataVencimento + 'T00:00:00');
  const nova = new Date(dataNovaVencimento + 'T00:00:00');
  const dias = Math.max(0, Math.floor((nova.getTime() - venc.getTime()) / (1000 * 60 * 60 * 24)));
  if (dias <= 0) return { juros: 0, multa: 0, dias: 0 };
  const multa = valor * (multaPercent / 100);
  const jurosDia = (jurosPercent / 100) / 30; // Juros calculado proporcionalmente ao mês comercial de 30 dias
  const juros = valor * jurosDia * dias;
  return { juros, multa, dias };
};

const RenegociarFaturaModal: React.FC<RenegociarFaturaModalProps> = ({
  open,
  onClose,
  invoiceIds,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [configuracao, setConfiguracao] = useState<{ juros: number; multa: number }>({ juros: 0, multa: 0 });
  const [clientId, setClientId] = useState<number | null>(null);

  const [dataVencimento, setDataVencimento] = useState(getCurrentDate());
  const [valorParcelas, setValorParcelas] = useState<number>(0);
  const [valorParcelasFormatado, setValorParcelasFormatado] = useState('0,00');
  const [acrescimo, setAcrescimo] = useState<number>(0);
  const [acrescimoFormatado, setAcrescimoFormatado] = useState('0,00');
  const [desconto, setDesconto] = useState<number>(0);
  const [descontoFormatado, setDescontoFormatado] = useState('0,00');
  const [contratoId, setContratoId] = useState<number | null>(null);
  const [contratoSelectDisabled, setContratoSelectDisabled] = useState(false);
  const [gatewayId, setGatewayId] = useState<number | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);

  const valorTotal = valorParcelas + acrescimo - desconto;

  useEffect(() => {
    if (open && invoiceIds.length > 0) {
      loadData();
    }
  }, [open, invoiceIds]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await invoiceService.getRenegociarDados(invoiceIds);
      if (response.success && response.data) {
        setInvoices(response.data.invoices);
        setConfiguracao(response.data.configuracao);
        const somaValor = response.data.invoices.reduce(
          (acc: number, inv: InvoiceData) => acc + inv.valor + (inv.acrescimo || 0) - (inv.desconto || 0),
          0
        );
        setValorParcelas(somaValor);
        setValorParcelasFormatado(formatarMoeda(somaValor));
        if (response.data.invoices[0]) {
          setClientId(response.data.invoices[0].cliente_id);
        }
        const contratoIds = (response.data.invoices as InvoiceData[])
          .map((inv) => inv.contrato_id)
          .filter((id): id is number => id != null && id > 0);
        const todasTemContrato = contratoIds.length === response.data.invoices.length;
        const uniqueContratos = [...new Set(contratoIds)];
        if (todasTemContrato && uniqueContratos.length === 1) {
          setContratoId(uniqueContratos[0]);
          setContratoSelectDisabled(true);
        } else {
          setContratoId(null);
          setContratoSelectDisabled(false);
        }
      } else {
        throw new Error(response.message || 'Erro ao carregar dados');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados de renegociação');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCalcularJurosMulta = () => {
    let totalAcrescimo = 0;
    invoices.forEach((inv) => {
      const { juros, multa } = calcularJurosMulta(
        inv.valor + (inv.acrescimo || 0) - (inv.desconto || 0),
        inv.data_vencimento,
        dataVencimento,
        configuracao.juros,
        configuracao.multa
      );
      totalAcrescimo += juros + multa;
    });
    const valorArredondado = Math.round(totalAcrescimo * 100) / 100;
    setAcrescimo(valorArredondado);
    setAcrescimoFormatado(formatarMoeda(valorArredondado));
  };

  const handleValorParcelasChange = (valor: string) => {
    const formatado = formatarValorOnInput(valor);
    setValorParcelasFormatado(formatado);
    setValorParcelas(parsearMoeda(formatado));
  };

  const handleAcrescimoChange = (valor: string) => {
    const formatado = formatarValorOnInput(valor);
    setAcrescimoFormatado(formatado);
    setAcrescimo(parsearMoeda(formatado));
  };

  const handleDescontoChange = (valor: string) => {
    const formatado = formatarValorOnInput(valor);
    setDescontoFormatado(formatado);
    setDesconto(parsearMoeda(formatado));
  };

  const handleGerar = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await invoiceService.renegociarFaturas({
        ids: invoiceIds,
        contrato_id: contratoId || undefined,
        data_vencimento: dataVencimento,
        valor: valorParcelas,
        acrescimo,
        desconto,
        gateway_id: gatewayId || undefined,
      });
      if (response.success) {
        onSuccess?.();
        onClose();
      } else {
        throw new Error(response.message);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao renegociar faturas');
      onError?.(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setError(null);
    setInvoices([]);
    setDataVencimento(getCurrentDate());
    setValorParcelas(0);
    setValorParcelasFormatado('0,00');
    setAcrescimo(0);
    setAcrescimoFormatado('0,00');
    setDesconto(0);
    setDescontoFormatado('0,00');
    setContratoId(null);
    setContratoSelectDisabled(false);
    setGatewayId(null);
    onClose();
  };

  const steps = ['Dados das Faturas', 'Configuração da Nova Fatura', 'Resumo e Gerar'];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ py: 1, px: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Renegociar Faturas</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 1, pt: 0 }}>
        <Stepper activeStep={step} sx={{ mb: 1.5, '& .MuiStepLabel-root': { py: 0 } }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 1, py: 0.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && invoices.length === 0 ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {step === 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Nº (ID)</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Emissão</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Vencimento</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Valor</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Juros</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Multa</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Total</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Descrição</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Mês Ref.</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Período</TableCell>
                      <TableCell sx={{ py: 0.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Gateway</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((inv) => {
                      const total = inv.valor + (inv.acrescimo || 0) - (inv.desconto || 0);
                      return (
                        <TableRow key={inv.id}>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>#{inv.id}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatarData(inv.data_emissao)}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatarData(inv.data_vencimento)}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatarValor(inv.valor)}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatarValor(inv.acrescimo || 0)}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>-</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatarValor(total)}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{inv.descricao}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{inv.mes_referencia || '-'}</TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {inv.data_inicial && inv.data_final
                              ? `${formatarData(inv.data_inicial)} - ${formatarData(inv.data_final)}`
                              : '-'}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{inv.gateway?.nome || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {step === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <GatewaySelect
                    value={gatewayId ?? ''}
                    size="small"
                    onChange={(id, gw) => {
                      setGatewayId(id);
                      setSelectedGateway(gw ?? null);
                    }}
                    label="Gateway de Pagamento"
                    sx={{ width: 395 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {clientId && clientId > 0 ? (
                    <ContractsActiveSelect
                      clientId={clientId}
                      value={contratoId}
                      onChange={(v) => setContratoId(typeof v === 'number' ? v : null)}
                      label="Contrato (opcional)"
                      placeholder="Nenhum contrato"
                      size="small"
                      sx={{ width: 395 }}
                      disabled={contratoSelectDisabled}
                    />
                  ) : null}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Data de Vencimento"
                    type="date"
                    value={dataVencimento}
                    onChange={(e) => setDataVencimento(e.target.value)}
                    sx={{ width: 395 }}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={2.8}>
                  <TextField
                    label="Valor Parcelas"
                    value={valorParcelasFormatado}
                    onChange={(e) => handleValorParcelasChange(e.target.value)}
                    size="small"

                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={2.8}>
                  <TextField
                    label="Desconto"
                    value={descontoFormatado}
                    onChange={(e) => handleDescontoChange(e.target.value)}
                    size="small"

                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      label="Acréscimo"
                      value={acrescimoFormatado}
                      onChange={(e) => handleAcrescimoChange(e.target.value)}
                      size="small"
                      sx={{ width: 180 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<CalculateIcon />}
                      onClick={handleCalcularJurosMulta}
                      size="small"
                    >
                      Calcular Juros/Multa
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 0.5 }}>
                    Valor Total: {formatarValor(valorTotal)}
                  </Typography>
                </Grid>
              </Grid>
            )}

            {step === 2 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ py: 0.5, minHeight: 180 }}
              >
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 6,
                    minWidth: 340,
                    px: 3,
                    py: 2,
                    textAlign: 'center'
                  }}
                  elevation={5}
                >
                  <CardContent>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                      Dados da nova fatura:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Data de Vencimento:</strong> {formatarData(dataVencimento)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Gateway:</strong> {selectedGateway?.nome || 'Nenhum'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Valor Total:</strong> {formatarValor(valorTotal)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1, gap: 0.5 }}>
        <Button onClick={handleClose}>Cancelar</Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={loading}>
            Voltar
          </Button>
        )}
        {step < 2 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={loading || invoices.length === 0}>
            Próximo
          </Button>
        ) : (
          <Button variant="contained" onClick={handleGerar} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Gerar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RenegociarFaturaModal;
